import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, User, ShieldCheck, House } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/Notifications";
import MenuDrawer from "@/components/MenuDrawer";

const iconStyle = { width: "4.6cqw", height: "4.6cqw", minWidth: "18px", minHeight: "18px" };

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const onAdmin = location.pathname.startsWith("/admin");

  const initials = user
    ? `${(user.first_name || user.name || " ").trim().charAt(0)}${(user.last_name || "").trim().charAt(0)}`.trim().toUpperCase() ||
      (user.name || "B").trim().charAt(0).toUpperCase()
    : null;

  return (
    <>
      <header className="sticky top-0 z-40 bg-[#FEF9F6]/95 backdrop-blur-md" style={{ paddingTop: "env(safe-area-inset-top, 0px)" }} data-testid="header">
        <div className="app-container">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center px-[4cqw] pt-[1.8cqw]">
            <div className="justify-self-start">
              <button
                onClick={() => setMenuOpen(true)}
                className="p-[1cqw] -ml-[1cqw] text-[#1C1917] hover:text-[#6B1226] transition-colors"
                data-testid="header-menu-button"
                aria-label="Ouvrir le menu"
              >
                <Menu style={iconStyle} strokeWidth={1.6} />
              </button>
            </div>

            <Link to="/" className="flex flex-col items-center" data-testid="header-brand-logo">
              <img
                src="/assets/mockup/logo-chateau.png"
                alt=""
                style={{ height: "8.7cqw", minHeight: "30px", width: "auto" }}
                className="object-contain no-frame"
              />
              <span className="font-logo-caps text-[2.6cqw] tracking-[0.34em] text-[#1C1917] mt-[1cqw] whitespace-nowrap">
                LES VINS DU
              </span>
              <span className="font-logo text-[5.5cqw] font-bold text-[#1C1917] tracking-[0.06em] leading-[1.05] whitespace-nowrap">
                BISTROQUET
              </span>
            </Link>

            <div className="flex items-center gap-[2cqw] justify-self-end">
              <NotificationBell />
              {user?.role === "admin" && (
                <button
                  onClick={() => navigate(onAdmin ? "/" : "/admin")}
                  className="rounded-full bg-[#F5EEDC] text-[#8C6D23] border border-[#C5A059]/60 flex items-center justify-center hover:bg-[#6B1226] hover:text-[#FAF6F0] hover:border-[#6B1226] transition-colors"
                  style={{ width: "7.4cqw", height: "7.4cqw", minWidth: "30px", minHeight: "30px" }}
                  data-testid="header-site-switch-button"
                  aria-label={onAdmin ? "Aller au site boutique" : "Aller au site administrateur"}
                >
                  {onAdmin ? (
                    <House style={{ width: "4cqw", height: "4cqw", minWidth: "15px", minHeight: "15px" }} strokeWidth={1.7} />
                  ) : (
                    <ShieldCheck style={{ width: "4cqw", height: "4cqw", minWidth: "15px", minHeight: "15px" }} strokeWidth={1.7} />
                  )}
                </button>
              )}
              <button
                onClick={() => navigate("/profil")}
                className="relative rounded-full overflow-hidden bg-[#6B1226] text-[#FAF6F0] border-2 border-[#C5A059]/60 shadow-md flex items-center justify-center hover:bg-[#580E1D] transition-colors"
                style={{ width: "8cqw", height: "8cqw", minWidth: "32px", minHeight: "32px" }}
                data-testid="header-profile-button"
                aria-label="Mon profil"
              >
                {user?.photo_url ? (
                  <img src={user.photo_url} alt={user.name} className="w-full h-full object-cover no-frame" />
                ) : initials ? (
                  <span className="font-eyebrow font-bold" style={{ fontSize: "3cqw", letterSpacing: "0.04em" }} data-testid="header-profile-initials">
                    {initials}
                  </span>
                ) : (
                  <User style={{ width: "4.2cqw", height: "4.2cqw", minWidth: "16px", minHeight: "16px" }} strokeWidth={1.6} />
                )}
              </button>
            </div>
          </div>

          <p
            className="text-center whitespace-nowrap font-eyebrow text-[2.2cqw] tracking-[0.24em] font-semibold text-[#57534E] mt-[3.2cqw] pb-[2cqw]"
            data-testid="header-tagline"
          >
            GRANDS VINS&nbsp;&nbsp;·&nbsp;&nbsp;BELLES RENCONTRES&nbsp;&nbsp;·&nbsp;&nbsp;ESPRIT BISTROT
          </p>
        </div>
      </header>
      <MenuDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}
